import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { AddToPlaylistModal } from "@/components/playlist/AddToPlaylistModal";
import { CoverImg } from "@/components/ui/CoverImg";
import {
  IconHeart, IconHeartFilled, IconMore, IconNext, IconPause, IconPlay,
  IconPrev, IconRepeat, IconRepeatOne, IconShuffle, IconShuffleSmart,
} from "@/components/ui/icons";
import { Marquee } from "@/components/ui/Marquee";
import { Slider } from "@/components/ui/Slider";
import { KaraokeView } from "./KaraokeView";
import { DevicesButton } from "./DevicesButton";
import { useLibrary } from "@/hooks/useLibrary";
import { usePlayer } from "@/hooks/usePlayer";
import { usePlayerProgress } from "@/hooks/usePlayerProgress";
import { sfx } from "@/lib/sfx";
import { fmtDuration } from "@/utils/format";

interface P { open: boolean; onClose: () => void }

/**
 * Full-screen "Now Playing" sheet for mobile. Reachable by tapping the
 * NowPlaying block in the compact mobile playbar. Big cover, big controls,
 * drag-down-to-dismiss gesture, portal-mounted so nothing else can trap it.
 */
export function NowPlayingSheet({ open, onClose }: P) {
  const {
    current, playing, toggle, next, prev, duration, seek,
    shuffle, smartShuffle, cycleShuffle, repeat, setRepeat, isRecommended, removeRecommended,
  } = usePlayer();
  // usePlayer() no longer streams live progress (moved to its own context
  // to avoid re-rendering every timeupdate). Pull it from the dedicated hook
  // otherwise the sheet's progress bar stays frozen at 0:00.
  const progress = usePlayerProgress();
  const { isLiked, toggle: toggleLib } = useLibrary();
  const [pickerOpen, setPickerOpen] = useState(false);

  const startY = useRef<number | null>(null);
  const dragY = useRef(0);
  const [pulled, setPulled] = useState(0);

  useEffect(() => {
    if (!open) return;
    const on = (e: KeyboardEvent) => { if (e.key === "Escape") { sfx.reverseSwoosh(); onClose(); } };
    window.addEventListener("keydown", on);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", on);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  const close = useCallback(() => { sfx.reverseSwoosh(); onClose(); }, [onClose]);

  const onTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length !== 1) return;
    startY.current = e.touches[0].clientY;
    dragY.current = 0;
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (startY.current === null) return;
    const dy = e.touches[0].clientY - startY.current;
    if (dy > 0) { dragY.current = dy; setPulled(Math.min(dy, 400)); }
  };
  const onTouchEnd = () => {
    if (dragY.current > 120) close();
    else setPulled(0);
    startY.current = null; dragY.current = 0;
  };

  if (!open || !current) return null;
  const liked = isLiked("track", current.id);
  const durFromAudio = Number.isFinite(duration) && duration > 0 ? duration : 0;
  const durFromTrack = current.duration_ms ? current.duration_ms / 1000 : 0;
  const total = durFromAudio || durFromTrack;
  const prog = Number.isFinite(progress) && progress >= 0 ? progress : 0;
  const known = total > 0;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      style={{
        position: "fixed", inset: 0, zIndex: 200,
        background: "linear-gradient(180deg, var(--accent-deep,#7a1d34) 0%, #0b0b0f 85%)",
        color: "#fff",
        display: "flex", flexDirection: "column",
        padding: "calc(env(safe-area-inset-top, 12px) + 12px) 20px calc(env(safe-area-inset-bottom, 12px) + 20px)",
        overflow: "hidden",
        transform: `translateY(${pulled}px)`,
        transition: pulled === 0 ? "transform .22s ease" : "none",
        animation: pulled === 0 ? "nps-fadein .25s ease-out" : undefined,
      }}
    >
      <style>{`
        @keyframes nps-fadein {
          from { transform: translateY(20px); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
      `}</style>

      {/* header row */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
        <button onClick={close} aria-label="Fermer"
                style={{ color: "#fff", padding: 6, fontSize: 20 }}>
          ⌄
        </button>
        <div style={{ textAlign: "center", opacity: 0.85, fontSize: 13, minWidth: 0 }}>
          <div style={{ fontWeight: 700, letterSpacing: 0.6 }}>Lecture en cours</div>
          <div style={{ fontSize: 11, opacity: 0.85, marginTop: 2 }} className="trunc">
            {current.album?.title ?? ""}
          </div>
        </div>
        <button aria-label="Plus"
                onClick={() => { sfx.tap(); setPickerOpen(true); }}
                style={{ color: "#fff", padding: 6 }}>
          <IconMore />
        </button>
      </div>

      {/* cover */}
      <div style={{
        flex: "1 1 auto", display: "flex", alignItems: "center", justifyContent: "center",
        margin: "16px 0",
      }}>
        <div style={{
          width: "min(88vw, 360px)", aspectRatio: "1 / 1",
          borderRadius: 14, overflow: "hidden",
          boxShadow: "0 25px 70px rgba(0,0,0,.55)",
        }}>
          <CoverImg coverId={current.album?.cover_id} alt={current.title}
                    size={Math.min(window.innerWidth * 0.88, 360)} />
        </div>
      </div>

      {/* meta */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Marquee style={{ fontSize: 24, fontWeight: 800, lineHeight: 1.15 }}>
            {current.title}
          </Marquee>
          <Link to={`/artist/${current.artist.id}`} onClick={close}
                style={{ color: "rgba(255,255,255,.75)", fontSize: 14, display: "block", marginTop: 2 }}>
            <Marquee>{current.artist.name}</Marquee>
          </Link>
          {isRecommended(current.id) && (
            <div className="rec-pill" style={{ marginTop: 6 }}>
              ✦ Recommandé
              <button onClick={() => { sfx.tap(); removeRecommended(current.id); }}
                      title="Retirer et ne plus proposer">✕ Retirer</button>
            </div>
          )}
        </div>
        <button aria-label={liked ? "Retirer des likés" : "Ajouter aux likés"}
                onClick={() => { if (liked) sfx.tap(); else sfx.chirp(); void toggleLib("track", current.id); }}
                style={{ color: liked ? "var(--accent)" : "#fff", padding: 6, fontSize: 24 }}>
          {liked ? <IconHeartFilled size={26} /> : <IconHeart size={26} />}
        </button>
      </div>

      {/* progress */}
      <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 4 }}>
        <Slider
          value={known ? Math.min(prog, total) : 0}
          max={known ? total : 1}
          step={0.1}
          onChange={seek}
          disabled={!known}
          ariaLabel="progression"
        />
        <div style={{
          display: "flex", justifyContent: "space-between",
          fontSize: 12, color: "rgba(255,255,255,.75)",
          fontVariantNumeric: "tabular-nums",
        }}>
          <span>{known ? fmtDuration(prog * 1000) : "--:--"}</span>
          <span>{known ? `-${fmtDuration(Math.max(0, (total - prog) * 1000))}` : "--:--"}</span>
        </div>
      </div>

      {/* transport row */}
      <div style={{
        display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr",
        alignItems: "center", justifyItems: "center",
        marginTop: 14,
      }}>
        <button aria-label="Aléatoire" title={smartShuffle ? "Aléatoire intelligent (des recommandations ✦ sont ajoutées) — cliquer pour désactiver"
                  : shuffle ? "Aléatoire — cliquer pour l'aléatoire intelligent" : "Aléatoire"}
                onClick={() => { sfx.tap(); cycleShuffle(); }}
                className={smartShuffle ? "shuffle-smart" : undefined}
                style={{ color: shuffle ? "var(--accent)" : "#fff", padding: 8 }}>
          {smartShuffle ? <IconShuffleSmart size={22} /> : <IconShuffle size={22} />}
        </button>
        <button aria-label="Précédent"
                onClick={() => { sfx.tap(); prev(); }}
                style={{ color: "#fff", padding: 8 }}>
          <IconPrev size={32} />
        </button>
        <button aria-label={playing ? "Pause" : "Lecture"}
                onClick={() => { sfx.pop(); toggle(); }}
                style={{
                  width: 72, height: 72, borderRadius: 999,
                  background: "#fff", color: "#000",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  boxShadow: "0 8px 24px rgba(0,0,0,.35)",
                }}>
          {playing ? <IconPause size={34} /> : <IconPlay size={34} />}
        </button>
        <button aria-label="Suivant"
                onClick={() => { sfx.tap(); next(); }}
                style={{ color: "#fff", padding: 8 }}>
          <IconNext size={32} />
        </button>
        <button aria-label="Répéter"
                onClick={() => { sfx.tap(); setRepeat(repeat === "off" ? "all" : repeat === "all" ? "one" : "off"); }}
                style={{ color: repeat !== "off" ? "var(--accent)" : "#fff", padding: 8 }}>
          {repeat === "one" ? <IconRepeatOne size={22} /> : <IconRepeat size={22} />}
        </button>
      </div>

      {/* footer — quick actions */}
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        marginTop: 18, opacity: 0.9,
      }}>
        <div className="nps-lyrics"><KaraokeView /></div>
        <div className="nps-lyrics" style={{ marginLeft: 8 }}><DevicesButton light /></div>
        <div style={{ fontSize: 12, flex: 1, marginLeft: 12 }}>
          <div style={{ fontWeight: 700, letterSpacing: 0.4 }}>Sur ta bibliothèque</div>
          <div style={{ opacity: 0.7 }}>Ajoute à une playlist</div>
        </div>
        <button aria-label="Ajouter à une playlist"
                onClick={() => { sfx.tap(); setPickerOpen(true); }}
                style={{
                  padding: "8px 14px", borderRadius: 999,
                  border: "1px solid rgba(255,255,255,.35)",
                  color: "#fff", fontSize: 20, lineHeight: 1,
                }}>+</button>
      </div>

      <AddToPlaylistModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        trackId={current.id}
      />
    </div>,
    document.body,
  );
}
